import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, Folder, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CreateMenuItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const ITEMS: CreateMenuItem[] = [
  { label: 'New Project', to: '/manager/create-project', icon: <Folder className="h-4 w-4" /> },
  { label: 'New Job', to: '/manager/create-job', icon: <Briefcase className="h-4 w-4" /> },
];

/**
 * Manager's source-compatible Create menu. Aligned renders this above the
 * manager sidebar for non-self-serve managers and routes each choice to a
 * first-class creation page.
 */
export function CreateMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Open create menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <Plus className="h-4 w-4" />
        New
      </button>

      {open ? (
        <div role="menu" className="absolute left-0 top-full z-20 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center text-slate-500">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
